import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import { ArrowRight, Leaf, Sparkles, ShieldCheck, Truck, ChevronDown, Sprout, HeartHandshake, FlaskConical } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

// Curated Unsplash lifestyle images (high fidelity, texture-forward)
const HERO_IMG = "https://images.unsplash.com/photo-1601049676869-702ea24cfd58?auto=format&fit=crop&w=1400&q=85";
const STORY_IMG = "https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?auto=format&fit=crop&w=1200&q=85";
const RITUAL_IMG = "https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?auto=format&fit=crop&w=1200&q=85";
const HERB_1 = "https://images.unsplash.com/photo-1611080626919-7cf5a9dbab5b?auto=format&fit=crop&w=800&q=85"; // fresh aloe
const HERB_2 = "https://images.unsplash.com/photo-1615485291234-9d694218aeb2?auto=format&fit=crop&w=800&q=85"; // neem leaves
const HERB_3 = "https://images.unsplash.com/photo-1583278554423-3d8d21f7050a?auto=format&fit=crop&w=800&q=85"; // hibiscus
const HERB_4 = "https://images.unsplash.com/photo-1615486511484-92e172cc4fe0?auto=format&fit=crop&w=800&q=85"; // turmeric root

// Slugs promoted in the "Trending" strip (per brand request)
const TRENDING_SLUGS = ["sanitary-pad", "neem-aloe-tulsi-face-wash", "ayurvedic-hair-oil", "day-night-cream"];

// FAQ content (also emitted as JSON-LD structured data for AEO / SEO)
const FAQ_ITEMS = [
  {
    q: "Are Roop Anand products 100% natural and Ayurvedic?",
    a: "Yes. Every Roop Anand product is formulated with plant-based Ayurvedic ingredients like Aloe Vera, Neem, Tulsi, Bhringraj, Amla and Reetha. We do not use parabens, SLS/SLES, artificial colours or animal testing.",
  },
  {
    q: "How long does delivery take?",
    a: "We ship all orders via Delhivery within 24 hours of confirmation. Most metros receive their orders in 2–4 business days; other pincodes typically deliver in 4–7 business days. You can track your order in real-time from the ‘My Orders’ section.",
  },
  {
    q: "Is Cash on Delivery available?",
    a: "Yes, Cash on Delivery is available on almost all serviceable pincodes across India. You can also pay securely via UPI, credit/debit cards and netbanking through PhonePe Business.",
  },
  {
    q: "What is your return policy?",
    a: "Unopened products can be returned within 7 days of delivery for a full refund. If your product arrives damaged or defective, please contact us at care@roopanand.in within 48 hours and we will arrange a free replacement.",
  },
  {
    q: "Are your products safe for sensitive skin?",
    a: "All Roop Anand products are dermatologically tested and made with gentle, skin-friendly ingredients. Because natural ingredients can occasionally cause reactions, we recommend a small patch test on the inner arm before first use.",
  },
  {
    q: "Where are Roop Anand products manufactured?",
    a: "Roop Anand products are handcrafted in small batches in India, following traditional Ayurvedic principles. Every batch is quality-tested before it leaves our facility.",
  },
];

// JSON-LD XSS defence: escape any '</' sequences in serialized JSON so
// no content can break out of the containing <script> tag.
const safeJsonLd = (obj) => JSON.stringify(obj).replace(/</g, "\\u003c");

function FaqSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_ITEMS.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: safeJsonLd(schema) }}
    />
  );
}

function OrgSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Roop Anand",
    url: typeof window !== "undefined" ? window.location.origin : "",
    logo: (typeof window !== "undefined" ? window.location.origin : "") + "/logo.png",
    description: "Roop Anand crafts Ayurvedic personal care products — hair oils, herbal shampoos, natural soaps, skincare — using time-tested Indian botanical wisdom.",
    sameAs: [],
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: safeJsonLd(schema) }}
    />
  );
}

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [trending, setTrending] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/products?featured=true").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/products").then((r) => {
      const bySlug = Object.fromEntries(r.data.map((p) => [p.slug, p]));
      setTrending(TRENDING_SLUGS.map((s) => bySlug[s]).filter(Boolean));
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <OrgSchema />

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-6 relative z-10">
            <p className="text-sm uppercase tracking-[0.2em] text-[#E65A28] mb-6 font-semibold">Ayurveda • Organic wellness</p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#0A192F] leading-[1.02]">
              Pure Ayurveda for<br />
              <span className="italic text-[#E65A28]">organic</span> wellness.
            </h1>
            <p className="mt-6 text-lg text-[#333] max-w-lg leading-relaxed">
              Small-batch herbal formulations rooted in ancient Indian wisdom — no parabens, no sulphates, no shortcuts. Only pure plants for your daily ritual.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 bg-[#E65A28] text-white px-8 py-4 rounded-full font-semibold hover:-translate-y-0.5 hover:shadow-lg transition-transform"
                data-testid="hero-shop-btn"
              >
                Shop the collection <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/category/hair-care"
                className="inline-flex items-center gap-2 border-2 border-[#0A192F] text-[#0A192F] px-8 py-4 rounded-full font-semibold hover:bg-[#0A192F] hover:text-white transition-colors"
                data-testid="hero-hair-btn"
              >
                Explore hair care
              </Link>
            </div>
            <div className="mt-12 flex flex-wrap gap-6 md:gap-8 text-sm">
              <div className="flex items-center gap-2"><Leaf className="h-4 w-4 text-[#E65A28]" /> 100% Herbal</div>
              <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#E65A28]" /> Handcrafted</div>
              <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#E65A28]" /> Derm-safe</div>
            </div>
          </div>
          <div className="lg:col-span-6 relative">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-[#F4EBE1] relative">
              <img src={HERO_IMG} alt="Ayurvedic wellness ritual" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0A192F]/10 via-transparent to-transparent" />
            </div>
            {/* Glass badge */}
            <div className="hidden md:flex absolute -bottom-6 -left-6 items-center gap-3 rounded-2xl p-5 shadow-xl border border-white/60"
                 style={{ background: "rgba(255,255,255,0.75)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)" }}>
              <div className="h-12 w-12 rounded-full bg-[#E65A28]/10 flex items-center justify-center">
                <HeartHandshake className="h-6 w-6 text-[#E65A28]" />
              </div>
              <div>
                <p className="text-2xl font-serif text-[#0A192F] leading-none">15k+</p>
                <p className="text-xs uppercase tracking-widest text-[#666] mt-1">Loyal customers</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TRENDING PRODUCTS ============ */}
      {trending.length > 0 && (
        <section className="relative py-16 md:py-20 bg-gradient-to-b from-transparent to-[#F4EBE1]/40">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <p className="text-sm uppercase tracking-[0.2em] text-[#E65A28] mb-2 font-semibold">Trending now</p>
                <h2 className="font-serif text-4xl md:text-5xl text-[#0A192F]">Loved this season</h2>
                <p className="mt-3 text-[#666] max-w-md">Four favourites our community keeps re-ordering, chosen for silk-soft skin and hair.</p>
              </div>
              <Link to="/shop" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A192F] hover:text-[#E65A28]">
                View all products <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {trending.map((p) => <ProductCard key={p.product_id} product={p} variant="glass" />)}
            </div>
          </div>
        </section>
      )}

      {/* ============ CATEGORIES - Bento ============ */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-[#E65A28] mb-2 font-semibold">Shop by ritual</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0A192F]">Curated categories</h2>
          </div>
          <Link to="/shop" className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-[#0A192F] hover:text-[#E65A28]">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-8 gap-6">
          {categories.map((cat, idx) => (
            <Link
              key={cat.category_id}
              to={`/category/${cat.slug}`}
              className={`group relative overflow-hidden rounded-3xl bg-[#F4EBE1] ${
                idx === 0 ? "md:col-span-5 md:row-span-2 aspect-[16/10]" : "md:col-span-3 aspect-[4/3]"
              }`}
              data-testid={`category-card-${cat.slug}`}
            >
              <img src={cat.image_url} alt={cat.name} className="absolute inset-0 h-full w-full object-cover product-img" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/70 via-[#0A192F]/20 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6 md:p-8 text-white">
                <p className="text-xs uppercase tracking-widest text-[#F9F6F0]/80 mb-1">{cat.description}</p>
                <h3 className="font-serif text-2xl md:text-3xl">{cat.name}</h3>
                <span className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[#E65A28] group-hover:gap-3 transition-all">
                  Explore <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ============ BESTSELLERS ============ */}
      <section className="bg-[#F4EBE1]/60 py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-[#E65A28] mb-2 font-semibold">Bestsellers</p>
              <h2 className="font-serif text-4xl md:text-5xl text-[#0A192F]">Loved by thousands</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
            {featured.slice(0, 8).map((p) => <ProductCard key={p.product_id} product={p} />)}
          </div>
        </div>
      </section>

      {/* ============ INGREDIENTS STORY ============ */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-sm uppercase tracking-[0.2em] text-[#E65A28] mb-2 font-semibold">Powered by nature</p>
          <h2 className="font-serif text-4xl md:text-5xl text-[#0A192F]">The heroes in every jar</h2>
          <p className="mt-4 text-[#666]">Each ingredient is chosen for its clinically proven Ayurvedic benefit — then sourced fresh, cold-pressed and blended in small batches.</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { img: HERB_1, title: "Aloe Vera", tag: "Hydration", text: "Cold-pressed leaves lock in moisture and calm irritated skin." },
            { img: HERB_2, title: "Neem", tag: "Purity", text: "Nature's antibacterial — clears blemishes without stripping." },
            { img: HERB_3, title: "Hibiscus", tag: "Radiance", text: "Boosts collagen and gives hair its dark, natural shine." },
            { img: HERB_4, title: "Turmeric", tag: "Glow", text: "The golden root for even tone, calm skin and daily glow." },
          ].map((h) => (
            <div key={h.title} className="rounded-3xl overflow-hidden bg-white shadow-[0_4px_20px_-8px_rgba(10,25,47,0.15)] hover-lift">
              <div className="aspect-[4/3] overflow-hidden bg-[#F4EBE1]">
                <img src={h.img} alt={h.title} className="w-full h-full object-cover product-img" />
              </div>
              <div className="p-5">
                <p className="text-[10px] uppercase tracking-widest text-[#E65A28] font-semibold">{h.tag}</p>
                <h3 className="font-serif text-2xl text-[#0A192F] mt-1">{h.title}</h3>
                <p className="text-sm text-[#666] mt-2 leading-relaxed">{h.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ RITUAL / HOW-TO ============ */}
      <section className="bg-[#0A192F] text-[#F9F6F0]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <p className="text-sm uppercase tracking-[0.2em] text-[#E65A28] mb-3 font-semibold">The Roop Anand ritual</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">A three-step daily practice for lasting radiance</h2>
            <p className="mt-5 text-[#F9F6F0]/80 leading-relaxed">
              Skincare isn&apos;t a shortcut — it&apos;s a slow, sensory ritual. Here&apos;s the sequence our founder swears by, morning and night.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { n: "01", icon: Sprout, title: "Cleanse", text: "Wash with our Neem Aloe Tulsi Face Wash — clears the day, keeps the moisture." },
              { n: "02", icon: FlaskConical, title: "Treat", text: "Target concerns with Anti Pimple Gel or Kumkumadi Day-Night Cream." },
              { n: "03", icon: Sparkles, title: "Nourish", text: "Massage in Ayurvedic Hair Oil twice a week, and drift into restful sleep." },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl p-6 border border-white/10"
                   style={{ background: "rgba(255,255,255,0.04)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }}>
                <p className="text-[#E65A28] font-mono text-sm">{s.n}</p>
                <s.icon className="h-6 w-6 mt-3 text-[#F9F6F0]" />
                <h3 className="font-serif text-2xl mt-3">{s.title}</h3>
                <p className="text-sm text-[#F9F6F0]/70 mt-2 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ BRAND STORY ============ */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="aspect-[4/5] rounded-3xl overflow-hidden">
          <img src={STORY_IMG} alt="Handcrafted with tradition" className="w-full h-full object-cover" />
        </div>
        <div>
          <p className="text-sm uppercase tracking-[0.2em] text-[#E65A28] mb-3 font-semibold">Our story</p>
          <h2 className="font-serif text-4xl md:text-5xl text-[#0A192F] leading-tight">
            Three generations. One promise — <span className="italic">pure Ayurveda.</span>
          </h2>
          <p className="mt-6 text-[#333] leading-relaxed text-lg">
            At Roop Anand, every drop, every leaf, every jar is a tribute to our grandmother&apos;s hands. We slow-cook oils in copper vessels, hand-pick herbs from the Himalayas, and refuse to compromise — because your ritual deserves reverence, not shortcuts.
          </p>
          <Link to="/shop" className="mt-8 inline-flex items-center gap-2 border-2 border-[#0A192F] text-[#0A192F] px-6 py-3 rounded-full font-semibold hover:bg-[#0A192F] hover:text-white transition-colors">
            Discover our craft <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* ============ FAQ (with JSON-LD schema markup) ============ */}
      <section className="bg-[#F4EBE1]/50 py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <p className="text-sm uppercase tracking-[0.2em] text-[#E65A28] mb-2 font-semibold">Frequently asked</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0A192F]">Questions, answered</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQ_ITEMS.map((it, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-2xl border-0 px-6 shadow-[0_1px_10px_-4px_rgba(10,25,47,0.08)]" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-serif text-xl text-[#0A192F] hover:no-underline py-5">
                  <span>{it.q}</span>
                  <ChevronDown className="h-5 w-5 shrink-0 text-[#E65A28] transition-transform duration-200" />
                </AccordionTrigger>
                <AccordionContent className="text-[#444] leading-relaxed pb-5 text-base">
                  <h3 className="sr-only">{it.q} — answer</h3>
                  {it.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <FaqSchema />
        </div>
      </section>

      {/* ============ USP ============ */}
      <section className="bg-[#0A192F] text-[#F9F6F0] py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { icon: Truck, title: "Free shipping", desc: "On orders above ₹499" },
            { icon: Leaf, title: "100% Ayurvedic", desc: "No parabens or SLS" },
            { icon: ShieldCheck, title: "Dermatologist-tested", desc: "Safe on all skin types" },
            { icon: Sparkles, title: "Handcrafted", desc: "In small batches" },
          ].map((f) => (
            <div key={f.title} className="flex flex-col items-start">
              <f.icon className="h-8 w-8 text-[#E65A28] mb-3" />
              <p className="font-serif text-xl">{f.title}</p>
              <p className="text-sm text-[#F9F6F0]/70 mt-1">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
